// Usa a mesma URL versionada emitida por global-script.liquid.
// Importar "./events.js" cria um segundo módulo sem versão, sujeito a cache antigo.
const eventsScript = document.querySelector('script[type="module"][src*="/events.js"]');
const { DiscountUpdateEvent } = await import(eventsScript.src);

let lastPostcodeValidation = null;

// Badge verde-escuro do cabeçalho de cupom e frete no carrinho lateral (texto via textContent).
const toolBadge = (text) => {
  const badge = document.createElement("span");
  badge.className = "dm-cart-tool-badge";
  const label = document.createElement("span");
  label.className = "dm-cart-tool-badge__text";
  label.textContent = text;
  badge.append(label);
  return badge;
};

class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.appliedDiscounts = new Set();
    const debug = false;
  }

  log(msg) {
    if (debug) {
      console.log("🛒 [CartDiscount]:", msg);
    }
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    this.error = this.querySelector(".discount-error");
    this.discountsList = this.querySelector(".applied-discounts-list");
    this.errorStorageKey = `cart-discount-error:${this.dataset.sectionId}`;
    const pendingError = sessionStorage.getItem(this.errorStorageKey);
    if (pendingError) {
      sessionStorage.removeItem(this.errorStorageKey);
      this.showDiscountError(pendingError);
    }
    // Carrinho lateral: um cupom por vez; aplicar outro código troca o atual.
    this.singleCode = this.hasAttribute("data-single-code");

    if (this.form) {
      this.form.addEventListener("submit", this.apply);
    }

    this.removeBtn = this.querySelector(".remove-discount");
    if (this.removeBtn) {
      this.removeBtn.addEventListener("click", this.clearAllDiscounts);
    }

    // Garantir que o campo input sempre venha limpo
    this.clearDiscountInput();

    // Carregar descontos salvos do localStorage
    this.loadSavedDiscounts();
  }

  disconnectedCallback() {
    if (this.form) this.form.removeEventListener("submit", this.apply);
    this.teardownRefazer();
  }

  async loadSavedDiscounts() {
    const saved = localStorage.getItem("appliedDiscounts");
    if (saved) {
      try {
        const discounts = JSON.parse(saved);
        this.appliedDiscounts = new Set(discounts);
      } catch (e) {
        console.error("Erro ao carregar descontos salvos:", e);
      }
    }

    // O refresh fornece a resposta confirmada antes de conectar o HTML novo.
    const snapshot = this.closest('.previewCart-wrapper')?.dmCartSnapshot;
    const cartData = await (snapshot || this.fetchCartData());
    this.syncWithCartDiscounts(cartData);
    await this.renderDiscountsList(cartData);
  }

  async fetchCartData() {
    try {
      const response = await fetch("/cart.js", {
        method: "GET",
        headers: { Accept: "application/json" },
        cache: "no-store",
      });

      return response.ok ? await response.json() : null;
    } catch (error) {
      console.error("Erro ao buscar dados do carrinho:", error);
      return null;
    }
  }

  syncWithCartDiscounts(cartData) {
    if (!cartData || !Array.isArray(cartData.discount_codes)) return;

    const currentDiscounts = new Set();
    cartData.discount_codes.forEach((discount) => {
      if (discount.applicable) currentDiscounts.add(discount.code);
    });

    this.appliedDiscounts = currentDiscounts;
    this.saveDiscounts();
  }

  saveDiscounts() {
    localStorage.setItem(
      "appliedDiscounts",
      JSON.stringify([...this.appliedDiscounts])
    );
  }

  clearDiscountInput() {
    const discountInput = this.form?.querySelector('[name="discount"]');
    if (discountInput) {
      discountInput.value = "";
    }
  }

  showDiscountError(message) {
    this.error.textContent = message;
    this.error.hidden = false;
    const details = this.closest("details");
    if (details) details.open = true;
    const toggle = this.closest("[data-dm-mobile-cart-tool]")?.querySelector(
      "[data-dm-mobile-cart-tool-toggle]"
    );
    if (toggle?.getAttribute("aria-expanded") === "false") toggle.click();
  }

  discountWasUsed(code) {
    const usedCodes = JSON.parse(this.dataset.usedDiscountCodes || "[]");
    return usedCodes.includes(code.toUpperCase());
  }

  apply = async (e) => {
    e.preventDefault();
    const discountCode = this.form
      .querySelector('[name="discount"]')
      .value.trim();

    if (!discountCode) {
      this.error.textContent = "Por favor, insira um código de desconto.";
      this.error.hidden = false;
      return;
    }

    if (this.discountWasUsed(discountCode)) {
      this.showDiscountError("Cupom já utilizado. Limite de 1 uso por CPF.");
      return;
    }

    if (this.appliedDiscounts.has(discountCode)) {
      this.error.textContent = "Este código de desconto já foi aplicado.";
      this.error.hidden = false;
      return;
    }

    // Adicionar estado de loading
    this.classList.add("is-updating");
    const submitButton =
      this.form.querySelector('button[type="submit"]') ||
      this.form.querySelector('input[type="submit"]');
    const originalButtonContent =
      submitButton?.innerHTML || submitButton?.value;

    if (submitButton) {
      submitButton.disabled = true;
      if (submitButton.innerHTML !== undefined) {
        submitButton.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="#ccc" stroke-width="2" fill="none" stroke-dasharray="32" stroke-dashoffset="32">
              <animate attributeName="stroke-dashoffset" values="32;0" dur="1s" repeatCount="indefinite"/>
            </circle>
          </svg>
        `;
      } else {
        submitButton.value = "Aplicando...";
      }
    }

    try {
      const allDiscounts = this.singleCode ? [] : [...this.appliedDiscounts];
      allDiscounts.push(discountCode);
      const discountString = allDiscounts.join(",");

      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          discount: discountString,
        }),
      });

      const data = await response.json();

      if (data.status && data.status !== 200) {
        this.error.textContent = data.description;
        this.error.hidden = false;
        return;
      }

      const submittedDiscount = data.discount_codes?.find(
        (discount) => discount.code.toUpperCase() === discountCode.toUpperCase()
      );
      const rejected = submittedDiscount?.applicable !== true;
      const rejectionMessage = "Este cupom não pode ser aplicado ao seu carrinho.";

      this.error.hidden = true;
      this.clearDiscountInput();

      // A resposta já contém o carrinho atualizado: reflete o código na faixa
      // imediatamente, sem duas leituras adicionais de /cart.js.
      this.syncWithCartDiscounts(data);
      await this.renderDiscountsList(data);

      document.dispatchEvent(
        new DiscountUpdateEvent(data, this.dataset.sectionId)
      );

      // Refresh que preserva as opções de frete já calculadas; cai no
      // updateCart padrão apenas se o refresh dedicado não estiver disponível.
      if (typeof window.diMangiareRefreshSideCart === "function") {
        await window.diMangiareRefreshSideCart(data);
      } else {
        window.updateCart();
      }

      // A atualização troca o componente lateral; mostra o aviso no elemento atual.
      if (rejected) {
        this.showDiscountError(rejectionMessage);
        document.querySelectorAll("cart-discount-component").forEach((component) => {
          if (component !== this && component.dataset.sectionId === this.dataset.sectionId) {
            component.showDiscountError(rejectionMessage);
          }
        });
      }

      // Na página completa, recarrega os totais e repõe o aviso uma única vez.
      if (window.location.pathname === "/cart") {
        if (rejected) sessionStorage.setItem(this.errorStorageKey, rejectionMessage);
        else sessionStorage.removeItem(this.errorStorageKey);
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      console.error(error);
      this.error.textContent = "Erro ao aplicar desconto. Tente novamente.";
      this.error.hidden = false;
    } finally {
      this.classList.remove("is-updating");
      // Restaurar botão
      if (submitButton) {
        submitButton.disabled = false;
        if (submitButton.innerHTML !== undefined) {
          submitButton.innerHTML = originalButtonContent || "Aplicar";
        } else {
          submitButton.value = originalButtonContent || "Aplicar";
        }
      }
    }
  };

  getDiscountPercentForCode(code, cartData) {
    if (!cartData || !code) return null;

    const upper = code.toUpperCase();
    const matches = (app) =>
      app && (app.title || "").toUpperCase() === upper;

    let percentageValue = null;

    // Descontos aplicados no nível do carrinho
    (cartData.cart_level_discount_applications || []).forEach((app) => {
      if (!matches(app)) return;
      if (app.value_type === "percentage" && app.value != null) {
        percentageValue = parseFloat(app.value);
      }
    });

    // Descontos aplicados linha a linha
    (cartData.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((alloc) => {
        const da = alloc.discount_application;
        if (!matches(da)) return;
        if (da.value_type === "percentage" && da.value != null) {
          percentageValue = parseFloat(da.value);
        }
      });
    });

    // Mostra apenas cupons de porcentagem (ignora os de valor fixo)
    if (percentageValue != null && !isNaN(percentageValue)) {
      return Math.round(percentageValue);
    }

    return null;
  }

  escapeHtml(value) {
    const node = document.createElement("span");
    node.textContent = String(value || "");
    return node.innerHTML;
  }

  async renderDiscountsList(cartData = null) {
    if (!this.discountsList) return;

    const toolAction = this.closest("[data-dm-mobile-cart-tool]")?.querySelector(
      ".dm-mobile-cart-tool__action"
    );

    if (this.appliedDiscounts.size === 0) {
      this.discountsList.innerHTML = "";
      this.discountsList.hidden = true;
      this.classList.remove("has-applied-discount");
      if (toolAction) {
        toolAction.classList.remove("is-value", "is-code");
        toolAction.textContent = "Inserir código";
      }
      return;
    }

    if (!cartData) cartData = await this.fetchCartData();

    const showRemoveLabel = this.hasAttribute("data-show-remove-label");

    const chips = [...this.appliedDiscounts].map((code) => {
      let isApplicable = true;
      if (cartData && Array.isArray(cartData.discount_codes)) {
        const discountInfo = cartData.discount_codes.find(
          (discount) => discount.code === code
        );
        isApplicable = discountInfo ? discountInfo.applicable : false;
      }
      // No carrinho lateral o cupom válido aparece só no badge; a pill fica para
      // avisar "Não aplicável" e permitir tirar esse código.
      if (this.singleCode && isApplicable) return "";

      const discountPercent = isApplicable
        ? this.getDiscountPercentForCode(code, cartData)
        : null;
      const safeCode = this.escapeHtml(code);

      return `
        <div class="discount-tag${isApplicable ? "" : " discount-tag--not-applicable"}">
          <span class="icon-discount-tag" aria-hidden="true">
            <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
              <path d="M1 4a3 3 0 0 0 3-3h12a3 3 0 0 0 3 3v8a3 3 0 0 0-3 3H4a3 3 0 0 0-3-3V4Z" stroke="currentColor" stroke-width="1.5"/>
              <path d="M10 2.5v11" stroke="currentColor" stroke-width="1.5" stroke-dasharray="2 2"/>
            </svg>
          </span>
          <span class="discount-code">${safeCode}</span>
          ${
            discountPercent != null && discountPercent > 0
              ? `<span class="discount-percent">-${discountPercent}%</span>`
              : ""
          }
          ${
            !isApplicable
              ? '<span class="discount-status">Não aplicável</span>'
              : ""
          }
          <button type="button" class="remove-single-discount" data-discount="${safeCode}" aria-label="Remover cupom ${safeCode}">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M4 7h16M9 7V4h6v3m-8 0 1 13h8l1-13M10 11v5m4-5v5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            ${showRemoveLabel ? '<span class="remove-single-discount-label">Remover</span>' : ""}
          </button>
        </div>
      `;
    });

    this.discountsList.innerHTML = `
      <div class="applied-discounts-container">
        <div class="discount-tags">${chips.join("")}</div>
      </div>
    `;
    this.discountsList.hidden = chips.every((chip) => chip === "");
    this.classList.add("has-applied-discount");

    const appliedCodes = [...this.appliedDiscounts];
    const appliedCode = appliedCodes[appliedCodes.length - 1];
    if (toolAction) {
      toolAction.classList.add("is-value", "is-code");
      toolAction.replaceChildren(toolBadge(String(appliedCode || "").toUpperCase()));
    }

    this.discountsList
      .querySelectorAll(".remove-single-discount")
      .forEach((button) => {
        button.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          const currentButton = event.currentTarget;
          this.removeSingleDiscount(currentButton.dataset.discount, currentButton);
        });
      });
  }

  removeSingleDiscount = async (discountCode, buttonElement = null) => {
    try {
      // Adicionar estado de loading ao botão
      if (buttonElement) {
        buttonElement.disabled = true;
        buttonElement.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="#ccc" stroke-width="2" fill="none" stroke-dasharray="32" stroke-dashoffset="32">
              <animate attributeName="stroke-dashoffset" values="32;0" dur="1s" repeatCount="indefinite"/>
            </circle>
          </svg>
        `;
      }

      // Remover da lista local
      this.appliedDiscounts.delete(discountCode);

      // Estratégia: limpar todos os cupons e reaplicar os restantes
      const remainingDiscounts = [...this.appliedDiscounts];

      // Primeiro, limpar todos os cupons
      await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ discount: "" }),
      });

      // Se houver cupons restantes, reaplica-los um por um com delay
      if (remainingDiscounts.length > 0) {
        for (let i = 0; i < remainingDiscounts.length; i++) {
          const code = remainingDiscounts[i];
          await fetch("/cart/update.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ discount: code }),
          });

          // Pequeno delay entre as aplicações para garantir processamento
          if (i < remainingDiscounts.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 300));
          }
        }
      }

      // Buscar dados atualizados do carrinho
      const finalResponse = await fetch("/cart.js", {
        method: "GET",
        headers: { Accept: "application/json" },
      });
      const data = await finalResponse.json();

      this.saveDiscounts();
      this.syncWithCartDiscounts(data);
      await this.renderDiscountsList(data);

      if (typeof window.diMangiareRefreshSideCart === "function") {
        await window.diMangiareRefreshSideCart(data);
      } else {
        window.updateCart();
      }

      document.dispatchEvent(
        new DiscountUpdateEvent(data, this.dataset.sectionId)
      );

      // Recarregar página se estiver na URL /cart
      if (window.location.pathname === "/cart") {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      console.error("Erro ao remover desconto:", error);
      this.appliedDiscounts.add(discountCode);
      this.saveDiscounts();

      if (buttonElement) {
        buttonElement.disabled = false;
        buttonElement.textContent = "Remover";
      }

    }
  };

  clearAllDiscounts = async (buttonElement = null) => {
    try {
      // Adicionar estado de loading ao botão
      if (buttonElement) {
        const originalContent = buttonElement.innerHTML;
        buttonElement.disabled = true;
        buttonElement.innerHTML = `
          <span>Removendo...</span>
          <svg width="16" height="16" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="#ccc" stroke-width="2" fill="none" stroke-dasharray="32" stroke-dashoffset="32">
              <animate attributeName="stroke-dashoffset" values="32;0" dur="1s" repeatCount="indefinite"/>
            </circle>
          </svg>
        `;
      }

      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ discount: "" }),
      });

      const data = await response.json();

      this.appliedDiscounts.clear();
      this.saveDiscounts();
      await this.renderDiscountsList(data);

      document.dispatchEvent(
        new DiscountUpdateEvent(data, this.dataset.sectionId)
      );

      if (typeof window.diMangiareRefreshSideCart === "function") {
        await window.diMangiareRefreshSideCart(data);
      } else {
        window.updateCart();
      }

      // Recarregar página se estiver na URL /cart
      if (window.location.pathname === "/cart") {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      console.error(error);

      // Restaurar botão em caso de erro
      if (buttonElement) {
        buttonElement.disabled = false;
        buttonElement.innerHTML = `
          <span>Remover todos</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 18" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M10.499 7.68726C10.8097 7.68726 11.0615 7.9391 11.0615 8.24976V12.7498C11.0615 13.0604 10.8097 13.3123 10.499 13.3123C10.1884 13.3123 9.93652 13.0604 9.93652 12.7498V8.24976C9.93652 7.9391 10.1884 7.68726 10.499 7.68726Z" fill="#00a0b1"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M7.50098 7.68726C7.81164 7.68726 8.06348 7.9391 8.06348 8.24976V12.7498C8.06348 13.0604 7.81164 13.3123 7.50098 13.3123C7.19032 13.3123 6.93848 13.0604 6.93848 12.7498V8.24976C6.93848 7.9391 7.19032 7.68726 7.50098 7.68726Z" fill="#00a0b1"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M4.5 4.68774C4.81066 4.68774 5.0625 4.93958 5.0625 5.25024V14.2502C5.0625 14.768 5.48223 15.1877 6 15.1877H12C12.5178 15.1877 12.9375 14.768 12.9375 14.2502V5.25024C12.9375 4.93958 13.1893 4.68774 13.5 4.68774C13.8107 4.68774 14.0625 4.93958 14.0625 5.25024V14.2502C14.0625 15.3893 13.1391 16.3127 12 16.3127H6C4.86091 16.3127 3.9375 15.3893 3.9375 14.2502V5.25024C3.9375 4.93958 4.18934 4.68774 4.5 4.68774Z" fill="#00a0b1"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M2.43848 5.25024C2.43848 4.93958 2.69032 4.68774 3.00098 4.68774H15.001C15.3116 4.68774 15.5635 4.93958 15.5635 5.25024C15.5635 5.5609 15.3116 5.81274 15.001 5.81274H3.00098C2.69032 5.81274 2.43848 5.5609 2.43848 5.25024Z" fill="#00a0b1"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M6.24798 1.99844C6.34327 1.80788 6.53804 1.6875 6.7511 1.6875H11.2511C11.4642 1.6875 11.6589 1.80788 11.7542 1.99844L13.2542 4.99844C13.3931 5.27631 13.2805 5.61418 13.0027 5.75312C12.7248 5.89205 12.3869 5.77942 12.248 5.50156L10.9035 2.8125H7.09874L5.75422 5.50156C5.61528 5.77942 5.27741 5.89205 4.99954 5.75312C4.72168 5.61418 4.60905 5.27631 4.74798 4.99844L6.24798 1.99844Z" fill="#00a0b1"></path>
          </svg>
        `;
      }
    }
  };

  teardownRefazer() {
    // Método de limpeza para a classe CartDiscount
    // Por enquanto vazio, pode ser expandido conforme necessário
  }
}

customElements.define("cart-discount-component", CartDiscount);

class CartShipping extends HTMLElement {
  fmtBRL(v) {
    const cents = Math.round((Number(v) || 0) * 100);
    if (window.Shopify && typeof Shopify.formatMoney === "function") {
      return Shopify.formatMoney(cents, window.money_format);
    }
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(cents / 100);
  }

  fmtBRLCurrency(v) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(Number(v) || 0);
  }

  /**
   * Prazo do frete em dias úteis ("3 dias úteis", "3–5 dias úteis").
   * A Shopify manda o intervalo em dias ou em datas; datas viram dias úteis
   * (segunda a sexta, sem feriados) contados a partir de hoje. Datas sem hora
   * ("2026-10-02") são lidas no fuso local: como UTC, a meia-noite cairia no dia
   * anterior no Brasil.
   */
  deliveryHtml(range) {
    if (!range) return "";
    const compactDeadline = Boolean(this.closest("#halo-cart-sidebar")) || this.hasAttribute("data-cart-page-summary");
    if (typeof range === "string") {
      return compactDeadline
        ? range.replace(/^(?:entrega\s+)?em\s+(?:at[eé]\s+)?/i, "").replace(/dias\s+[uú]teis/gi, "dias úteis")
        : range;
    }

    const toDays = (v) => {
      if (typeof v === "number" && Number.isFinite(v)) return v;
      if (typeof v !== "string" || Number.isNaN(Date.parse(v))) return null;
      const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
      const day = dateOnly
        ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
        : new Date(v);
      const cursor = new Date();
      cursor.setHours(0, 0, 0, 0);
      day.setHours(0, 0, 0, 0);
      let businessDays = 0;
      while (cursor < day) {
        cursor.setDate(cursor.getDate() + 1);
        const weekday = cursor.getDay();
        if (weekday !== 0 && weekday !== 6) businessDays += 1;
      }
      return businessDays;
    };

    const min = toDays(range.min ?? range.minimum ?? range.from ?? range[0] ?? null);
    const max = toDays(range.max ?? range.maximum ?? range.to ?? range[1] ?? null);
    if (min == null && max == null) return "";

    const same = (min ?? max) === (max ?? min);
    const qtd = same ? (min ?? max) : `${min ?? "?"}–${max ?? "?"}`;
    return `${compactDeadline ? "" : "em "}${qtd} dias úteis`;
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    this.error = this.querySelector(".shipping-component-error");

    if (this.form) {
      this.form.addEventListener("submit", this.apply);
      this.form.addEventListener("input", this.onCepInput);
    }
    // Delegado no host: sobrevive à re-renderização da lista de opções
    this.addEventListener("change", this.onRateChange);

    const savedCep = (localStorage.getItem("lastCalculatedCep") || "").replace(
      /\D/g,
      ""
    );
    const savedRates = localStorage.getItem("shippingRates");

    // A Compra rápida em /cart recarrega a página; as tarifas salvas podem
    // pertencer à cesta anterior. Restaura a exibição e confirma uma nova cotação.
    if (savedCep.length === 8 && this.hasAttribute("data-cart-page-summary")) {
      const requestAtConnection = this.ratesRequest;
      queueMicrotask(() => {
        if (this.isConnected && this.ratesRequest === requestAtConnection) {
          this.autoRecalculateShipping();
        }
      });
    }

    // Sem CEP válido salvo: mantém tudo limpo (campo vazio, sem opções nem frete no total)
    if (savedCep.length !== 8 || !savedRates) {
      localStorage.removeItem("shippingRates");
      localStorage.removeItem("selectedShippingRate");
      return;
    }

    // Preenche o campo com o CEP que gerou as opções, para o estado ficar coerente
    const cepInput = this.form?.querySelector('[name="shipping-component"]');
    if (cepInput) {
      cepInput.value = `${savedCep.slice(0, 5)}-${savedCep.slice(5)}`;
      cepInput.dataset.cepDigits = savedCep;
      cepInput.dataset.cepMask = cepInput.value;
    }

    const rates = JSON.parse(savedRates);
    rates.sort((a, b) => a.price - b.price);

    const selectedIndex = this.selectedRateIndex(rates);

    this.saveSelectedRate(rates[selectedIndex]);
    this.renderOptions(rates, selectedIndex);
    this.updateShippingListValue(rates[selectedIndex].price, false, rates[selectedIndex].name, "restore");
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener("submit", this.apply);
      this.form.removeEventListener("input", this.onCepInput);
    }
    this.removeEventListener("change", this.onRateChange);
    clearTimeout(this.cartPageCollapseTimer);
    this.ratesRequest += 1;
  }

  // A página completa usa <details>; o drawer conserva seu controlador próprio.
  scheduleCartPageCollapse(delay) {
    clearTimeout(this.cartPageCollapseTimer);
    if (!this.hasAttribute("data-cart-page-summary")) return;
    const tool = this.closest("details.cart-summary-tool--shipping");
    if (!tool?.open) return;
    this.cartPageCollapseTimer = setTimeout(() => {
      if (!this.isConnected || !tool.open) return;
      if (!this.querySelector(".shipping-option-radio:checked")) return;
      const focusWasInside = tool.contains(document.activeElement);
      tool.open = false;
      if (focusWasInside) tool.querySelector("summary")?.focus();
    }, delay);
  }

  // Qualquer mudança de destino invalida tarifas, total e consultas anteriores.
  onCepInput = (e) => {
    const input = e.target.closest('[name="shipping-component"]');
    if (!input) return;
    const raw = input.value;
    let digits = raw.replace(/\D/g, "");
    let before = raw.slice(0, input.selectionStart ?? raw.length).replace(/\D/g, "").length;
    // Apagar o separador deve apagar o dígito vizinho, sem prender o cursor.
    if (digits === input.dataset.cepDigits && raw !== input.dataset.cepMask &&
        /^deleteContent(Backward|Forward)$/.test(e.inputType || "")) {
      const index = e.inputType === "deleteContentBackward" ? before - 1 : before;
      if (index >= 0 && index < digits.length) {
        digits = digits.slice(0, index) + digits.slice(index + 1);
        if (e.inputType === "deleteContentBackward") before -= 1;
      }
    }
    const masked = digits.length > 5 ? digits.slice(0, 5) + "-" + digits.slice(5) : digits;
    input.value = masked;
    input.dataset.cepDigits = digits;
    input.dataset.cepMask = masked;
    const caret = Math.min(masked.length, before + (before > 5 ? 1 : 0));
    input.setSelectionRange(caret, caret);
    clearTimeout(this.cartPageCollapseTimer);
    const destination = this.pendingZip || localStorage.getItem("lastCalculatedCep") || "";
    if (digits !== destination || digits.length !== 8) this.clearRates();
    else if (this.error) this.error.hidden = true;
  };

  clearRates(sync = true) {
    this.ratesRequest += 1;
    this.pendingZip = "";
    clearTimeout(this.cartPageCollapseTimer);
    this.hideRecalculatingState();
    this.form?.querySelector(".form-loading")?.classList.add("hidden");
    localStorage.removeItem("shippingRates");
    localStorage.removeItem("selectedShippingRate");
    localStorage.removeItem("lastCalculatedCep");

    const listBox = this.querySelector(".selected-shipping-components");
    const wrapper = listBox?.querySelector(".shipping-wrapper");
    if (wrapper) wrapper.innerHTML = "";
    listBox?.classList.add("hidden");

    if (this.error) this.error.hidden = true;
    this.updateShippingListValue(0, true);
    if (sync) {
      document.querySelectorAll("cart-shipping-component").forEach((component) => {
        if (component !== this) component.clearRates(false);
      });
    }
  }

  // Re-soma o frete escolhido ao total quando os totais são reescritos por um
  // refresh (ex.: aplicar cupom) sem que este componente seja recriado.
  reapplyShipping() {
    const cep = (localStorage.getItem("lastCalculatedCep") || "").replace(
      /\D/g,
      ""
    );
    const selected = localStorage.getItem("selectedShippingRate");
    if (cep.length !== 8 || !selected) return;

    try {
      const saved = JSON.parse(selected);
      this.updateShippingListValue(saved.price, false, saved.name);
    } catch (e) {}
  }

  apply = async (e) => {
    e.preventDefault();

    const raw =
      this.form.querySelector('[name="shipping-component"]')?.value || "";
    const zip = raw.replace(/\D/g, "");
    this.clearRates();
    if (zip.length !== 8) {
      this.error.textContent = "Informe um CEP válido (8 dígitos).";
      this.error.hidden = false;
      return;
    }

    const spinner = this.form.querySelector(".form-loading");
    spinner.classList.remove("hidden");
    this.error.hidden = true;
    const request = this.ratesRequest + 1;

    try {
      const completed = await this.calculateShippingRates(zip);
      if (!completed) return;
      if (this.querySelector(".shipping-option-radio:checked")) {
        this.dispatchEvent(new CustomEvent("shipping:options-ready", { bubbles: true }));
      }

      // O resumo completo já é atualizado sem reload; preserva a escolha aberta.
      this.scheduleCartPageCollapse(2000);
    } catch (err) {
      this.clearRates();
      console.error(err);
      this.error.textContent =
        err.message || "Erro ao consultar frete. Tente novamente.";
      this.error.hidden = false;
    } finally {
      if (request === this.ratesRequest) spinner.classList.add("hidden");
    }
  };

  // Consulta até a Shopify concluir o cálculo; falhas e resultados vazios
  // precisam chegar ao formulário em vez de terminarem silenciosamente.
  pollRates = async (url, { tries = 12, delay = 500 } = {}) => {
    let lastPayload = null;

    for (let i = 0; i < tries; i++) {
      const res = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
      });
      const data = await res.json().catch(() => ({}));
      lastPayload = data;

      if (!res.ok) {
        throw new Error(
          this.shippingErrorMessage(
            data,
            `Não foi possível consultar o frete (erro ${res.status}).`
          )
        );
      }

      if (Array.isArray(data?.shipping_rates)) {
        if (data.shipping_rates.length) return data.shipping_rates;
        throw new Error("Nenhuma opção de frete encontrada para este CEP.");
      }

      if (i < tries - 1) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    throw new Error(
      this.shippingErrorMessage(
        lastPayload,
        "A consulta de frete demorou mais que o esperado. Tente novamente."
      )
    );
  };

  updateCartPageSummary(price, removed = false) {
    if (!this.hasAttribute("data-cart-page-summary")) return;

    const summary = this.closest("[data-cart-total]");
    if (!summary) return;

    const shippingPrice = removed ? 0 : Number(price) || 0;
    summary.querySelectorAll("[data-cart-shipping-value]").forEach((value) => {
      value.classList.toggle("is-empty", removed);
      value.textContent = removed
        ? value.dataset.shippingEmpty || "A calcular"
        : shippingPrice === 0
          ? "Grátis"
          : this.fmtBRLCurrency(shippingPrice);
    });

    summary
      .querySelectorAll("[data-cart-total-value][data-cart-base-total]")
      .forEach((value) => {
        const baseTotal = Number(value.dataset.cartBaseTotal) || 0;
        const target = value.querySelector(".text") || value;
        target.textContent = this.fmtBRL(baseTotal / 100 + shippingPrice);
      });
  }

  // Emite um evento para atualizar a listagem de preços. Cf. events.js:40
  // No cabeçalho do frete, a opção escolhida aparece só como badge (o valor está no total).
  updateShippingListValue(price, removed = false, rateName = "", source = "selection") {
    const toolAction = this.closest("[data-dm-mobile-cart-tool]")?.querySelector(
      ".dm-mobile-cart-tool__action"
    );
    if (toolAction) {
      toolAction.classList.toggle("is-value", !removed);
      if (!removed && rateName) {
        toolAction.replaceChildren(toolBadge(rateName));
      } else {
        toolAction.textContent = removed
          ? this.hasAttribute("data-cart-page-summary") ? "Inserir CEP" : "Calcule seu frete"
          : Number(price) === 0
            ? "Grátis"
            : this.hasAttribute("data-cart-page-summary")
              ? this.fmtBRLCurrency(price)
              : this.fmtBRL(price);
      }
    }

    this.updateCartPageSummary(price, removed);

    document.dispatchEvent(
      new CustomEvent("shipping:update", {
        detail: { price, fmtBRL: this.fmtBRL, removed, source },
        bubbles: true,
        composed: true,
      })
    );
  }

  // Recalcula após cart-update/discount:update, também no resumo da página /cart.
  autoRecalculateShipping = async () => {
    const savedCep = (localStorage.getItem("lastCalculatedCep") || "").replace(/\D/g, "");
    if (savedCep.length !== 8) return;

    this.showRecalculatingState();
    const cepInput = this.form?.querySelector('[name="shipping-component"]');
    if (cepInput) {
      cepInput.value = `${savedCep.slice(0, 5)}-${savedCep.slice(5)}`;
      cepInput.dataset.cepDigits = savedCep;
      cepInput.dataset.cepMask = cepInput.value;
    }

    const request = this.ratesRequest + 1;
    try {
      await this.calculateShippingRates(savedCep);
    } catch (error) {
      // Uma consulta antiga não pode apagar a cotação mais recente.
      if (request !== this.ratesRequest) return;
      this.clearRates();
      this.error.textContent = error.message || "Erro ao consultar frete. Tente novamente.";
      this.error.hidden = false;
    } finally {
      if (request === this.ratesRequest) this.hideRecalculatingState();
    }
  };

  // Métodos para controlar a animação de recálculo
  showRecalculatingState = () => {
    document.dispatchEvent(new CustomEvent("shipping:recalculating"));
    this.createTemporarySkeleton();
  };

  hideRecalculatingState = () => {
    // this.log("🎨 hideRecalculatingState chamado");
    this.classList.remove("shipping-recalculating");
    this.removeTemporarySkeleton();
  };

  createTemporarySkeleton = () => {
    // this.log("🔧 createTemporarySkeleton chamado");
    const listBox = this.querySelector(".selected-shipping-components");
    if (!listBox || listBox.querySelector(".shipping-skeleton-temp")) {
      //   this.log("❌ listBox não encontrado ou skeleton já existe");
      return;
    }

    const skeletonHtml = `
      <div class="shipping-skeleton-temp" style="padding: 10px 0;">
        <div class="shipping-skeleton wide"></div>
        <div class="shipping-skeleton medium"></div>
        <div class="shipping-skeleton narrow"></div>
      </div>
    `;

    // this.log("✅ Inserindo skeleton HTML");
    listBox.insertAdjacentHTML("beforeend", skeletonHtml);
    listBox.classList.remove("hidden");
  };

  removeTemporarySkeleton = () => {
    // this.log("🗑️ removeTemporarySkeleton chamado");
    const skeleton = this.querySelector(".shipping-skeleton-temp");
    if (skeleton) {
      //   this.log("✅ Removendo skeleton");
      skeleton.remove();
    } else {
      //   this.log("❌ Skeleton não encontrado para remover");
    }
  };

  // Oito números e faixa de UF não comprovam que o CEP existe.
  async validatePostcode(zip) {
    // Compartilhado entre instâncias: o AJAX recria o componente do drawer.
    // Só o último CEP é reutilizado; trocar o CEP inicia outra validação.
    if (lastPostcodeValidation?.zip === zip) {
      return lastPostcodeValidation.promise;
    }

    const validation = { zip, promise: null };
    lastPostcodeValidation = validation;
    validation.promise = this.fetchPostcodeValidation(zip).catch((error) => {
      // Falhas não ficam salvas e uma resposta antiga não invalida o CEP novo.
      if (lastPostcodeValidation === validation) lastPostcodeValidation = null;
      throw error;
    });
    return validation.promise;
  }

  async fetchPostcodeValidation(zip) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch("https://viacep.com.br/ws/" + zip + "/json/", {
        signal: controller.signal,
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error("Não foi possível consultar o CEP. Tente novamente.");
      const data = await response.json();
      if (data?.erro) throw new Error("CEP não encontrado. Confira os números e tente novamente.");
      if (!data?.uf || String(data.cep || "").replace(/\D/g, "") !== zip) {
        throw new Error("Não foi possível consultar o CEP. Tente novamente.");
      }
    } catch (error) {
      if (error.name === "AbortError" || error instanceof TypeError || error instanceof SyntaxError) {
        throw new Error("Não foi possível consultar o CEP. Tente novamente.");
      }
      throw error;
    } finally {
      clearTimeout(timer);
    }
  }

  provinceForZip(zip) {
    const prefix = Number(String(zip || "").replace(/\D/g, "").slice(0, 5));
    if (!Number.isFinite(prefix)) return "";

    const ranges = [
      [1000, 19999, "Sao Paulo"],
      [20000, 28999, "Rio de Janeiro"],
      [29000, 29999, "Espirito Santo"],
      [30000, 39999, "Minas Gerais"],
      [40000, 48999, "Bahia"],
      [49000, 49999, "Sergipe"],
      [50000, 56999, "Pernambuco"],
      [57000, 57999, "Alagoas"],
      [58000, 58999, "Paraiba"],
      [59000, 59999, "Rio Grande do Norte"],
      [60000, 63999, "Ceara"],
      [64000, 64999, "Piaui"],
      [65000, 65999, "Maranhao"],
      [66000, 68899, "Para"],
      [68900, 68999, "Amapa"],
      [69000, 69299, "Amazonas"],
      [69300, 69399, "Roraima"],
      [69400, 69899, "Amazonas"],
      [69900, 69999, "Acre"],
      [70000, 72799, "Distrito Federal"],
      [72800, 72999, "Goias"],
      [73000, 73699, "Distrito Federal"],
      [73700, 76799, "Goias"],
      [76800, 76999, "Rondonia"],
      [77000, 77999, "Tocantins"],
      [78000, 78899, "Mato Grosso"],
      [79000, 79999, "Mato Grosso do Sul"],
      [80000, 87999, "Parana"],
      [88000, 89999, "Santa Catarina"],
      [90000, 99999, "Rio Grande do Sul"],
    ];

    return ranges.find(([min, max]) => prefix >= min && prefix <= max)?.[2] || "";
  }

  shippingErrorMessage(payload, fallback) {
    const detail = payload?.errors ?? payload?.error ?? payload?.message;
    if (typeof detail === "string" && detail.trim()) return detail.trim();
    if (Array.isArray(detail)) {
      const message = detail.filter((item) => typeof item === "string").join(" ");
      if (message) return message;
    }
    if (detail && typeof detail === "object") {
      const message = Object.values(detail)
        .flat()
        .find((item) => typeof item === "string" && item.trim());
      if (message) return message.trim();
    }
    return fallback;
  }

  // Consultas de frete em andamento: clearRates() incrementa para descartar respostas
  // que chegam depois da remoção (senão elas escolheriam o frete de novo).
  ratesRequest = 0;

  calculateShippingRates = async (zip) => {
    const request = ++this.ratesRequest;
    this.pendingZip = zip;
    try {
      if (!/^\d{8}$/.test(zip)) throw new Error("Informe um CEP válido (8 dígitos).");
      await this.validatePostcode(zip);
      if (request !== this.ratesRequest) return false;
      const root = window.Shopify?.routes?.root || "/";
      const province = this.provinceForZip(zip);
      if (!province) {
        throw new Error("Não foi possível identificar o estado deste CEP.");
      }

      const shippingAddress = {
        zip,
        country: "Brazil",
        province,
      };
      const params = new URLSearchParams({
        "_easygift_internal": "true",
        "shipping_address[zip]": shippingAddress.zip,
        "shipping_address[country]": shippingAddress.country,
        "shipping_address[province]": shippingAddress.province,
      });

      // Mantém o preparo que já existia no fluxo estável da loja.
      const prepare = await fetch(
        `${root}cart/prepare_shipping_rates.json`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ shipping_address: shippingAddress }),
        }
      );

      if (!prepare.ok) {
        const err = await prepare.json().catch(() => ({}));
        throw new Error(
          this.shippingErrorMessage(
            err,
            "Não foi possível iniciar o cálculo de frete."
          )
        );
      }
      // Removido no meio do preparo: nem começa as consultas de resultado.
      if (request !== this.ratesRequest) return false;

      // A integração da loja usa este endpoint síncrono e o marcador interno.
      const rates = await this.pollRates(
        `${root}cart/shipping_rates.json?${params}`
      );
      if (request !== this.ratesRequest) return false;

      if (!rates.length) {
        throw new Error("Nenhuma opção de frete encontrada para este CEP.");
      }

      rates.sort((a, b) => a.price - b.price);
      localStorage.setItem("lastCalculatedCep", zip);
      localStorage.setItem("shippingRates", JSON.stringify(rates));

      const selectedIndex = this.selectedRateIndex(rates, true);

      this.saveSelectedRate(rates[selectedIndex]);
      this.renderOptions(rates, selectedIndex);
      this.updateShippingListValue(rates[selectedIndex].price, false, rates[selectedIndex].name, "quote");
      this.pendingZip = "";
      return true;
    } catch (error) {
      if (request !== this.ratesRequest) return false;
      this.pendingZip = "";
      throw error;
    }
  };

  // Mantém o serviço escolhido mesmo quando o cupom muda seu preço.
  // Na cotação nova, a escolha automática prioriza uma tarifa grátis.
  // Uma opção escolhida pelo cliente permanece vinculada ao mesmo serviço.
  selectedRateIndex(rates, preferFree = false) {
    let selected = null;
    try {
      selected = JSON.parse(localStorage.getItem("selectedShippingRate") || "null");
    } catch (error) {}
    const manualServiceAvailable = selected?._dmManualSelection === true &&
      rates.some((rate) => rate.name === selected.name);
    if (preferFree && !manualServiceAvailable) {
      const free = rates.findIndex((rate) => Number(rate.price) === 0);
      if (free >= 0) return free;
    }
    if (!selected) return 0;
    const exact = rates.findIndex(
      (rate) => rate.name === selected.name && Number(rate.price) === Number(selected.price)
    );
    if (exact >= 0) return exact;
    const sameService = rates.findIndex((rate) => rate.name === selected.name);
    return sameService >= 0 ? sameService : 0;
  }

  saveSelectedRate(rate, manual = false) {
    let previous = null;
    try {
      previous = JSON.parse(localStorage.getItem("selectedShippingRate") || "null");
    } catch (error) {}
    localStorage.setItem("selectedShippingRate", JSON.stringify({
      ...rate,
      _dmManualSelection: manual ||
        (previous?._dmManualSelection === true && previous.name === rate.name)
    }));
  }

  // Renderiza todas as opções de frete como radios; o cliente escolhe a entrega.
  renderOptions(rates, selectedIndex) {
    const listBox = this.querySelector(".selected-shipping-components");
    const wrapper = listBox?.querySelector(".shipping-wrapper");
    if (!wrapper) return;

    const radioName = `cart-shipping-rate-${this.dataset.sectionId || "cd"}`;

    wrapper.innerHTML = rates
      .map((rate, i) => {
        const prazo = this.deliveryHtml(rate.delivery_range);
        const price =
          Number(rate.price) === 0 ? "Grátis" : this.fmtBRL(rate.price);
        const checked = i === selectedIndex ? " checked" : "";
        return `
          <label class="shipping-option">
            <input type="radio" name="${radioName}" class="shipping-option-radio" value="${rate.price}" data-rate-index="${i}"${checked}>
            <span class="shipping-name">${rate.name}</span>
            ${prazo ? `<span class="shipping-date">${prazo}</span>` : ""}
            <span class="shipping-price">${price}</span>
          </label>
        `;
      })
      .join("");

    wrapper.style.display = "";
    listBox.classList.remove("hidden");
  }

  // Ao trocar de opção, salva a seleção e atualiza o total do carrinho lateral.
  onRateChange = (e) => {
    const radio = e.target.closest(".shipping-option-radio");
    if (!radio) return;

    const index = parseInt(radio.dataset.rateIndex, 10);
    const rates = JSON.parse(localStorage.getItem("shippingRates") || "[]");
    const rate = rates[index];
    if (!rate) return;

    this.saveSelectedRate(rate, true);
    this.updateShippingListValue(rate.price, false, rate.name);
    this.scheduleCartPageCollapse(1000);
  };
}

customElements.define("cart-shipping-component", CartShipping);
